import React, { useState, useEffect } from 'react';
import { formatearMoneda } from '../utils/moneda';
import { obtenerTodosAnteojos, eliminarAnteojo, editarAnteojo } from '../api/backend_v2';
import Swal from 'sweetalert2';
import AnteojoDetalle from './AnteojoDetalle';
import { formatearFechaArgentina } from '../utils/fechas';
import {
  EyeIcon,
  PhoneIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  ChatBubbleLeftEllipsisIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const AnteojosTodos = () => {
  const [anteojos, setAnteojos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detalleAnteojo, setDetalleAnteojo] = useState(null);

  useEffect(() => {
    cargarAnteojos();
    // eslint-disable-next-line
  }, []);

  const cargarAnteojos = async () => {
    setLoading(true);
    try {
      const data = await obtenerTodosAnteojos();
      setAnteojos(data.anteojos || []);
      setError(null);
    } catch (err) {
      setError('Error al cargar los anteojos');
    }
    setLoading(false);
  };

  const handleEliminarAnteojo = async (id) => {
    const result = await Swal.fire({
      title: '¿Eliminar anteojo?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });
    if (result.isConfirmed) {
      try {
        await eliminarAnteojo(id);
        cargarAnteojos();
        Swal.fire('Eliminado', 'El anteojo ha sido eliminado.', 'success');
      } catch (err) {
        Swal.fire('Error', 'No se pudo eliminar el anteojo.', 'error');
      }
    }
  };

  const formatearFecha = formatearFechaArgentina;

  const getEstadoColor = (estado) => {
    const colors = {
      'Tomado': 'bg-blue-200 text-blue-800',
      'Preparado': 'bg-yellow-200 text-yellow-800',
      'En laboratorio': 'bg-purple-200 text-purple-800',
      'Para retirar': 'bg-orange-200 text-orange-800',
      'Entregado': 'bg-green-200 text-green-800',
      'Anulado': 'bg-red-200 text-red-800',
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl font-bold mb-6 text-blue-700">Todos los Anteojos</h2>
        {loading ? (
          <div className="text-center py-10 text-blue-500">Cargando anteojos...</div>
        ) : error ? (
          <div className="text-center py-10 text-red-500">{error}</div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {anteojos.map(anteojo => (
              <li key={anteojo.id}>
                <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    {/* Info */}
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <EyeIcon className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">{anteojo.cliente.nombre} {anteojo.cliente.apellido}</span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{anteojo.tipo_anteojo}</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getEstadoColor(anteojo.estado)}`}>{anteojo.estado}</span>
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <PhoneIcon className="h-3.5 w-3.5 flex-shrink-0" />
                            {anteojo.cliente.telefono}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <CalendarIcon className="h-3.5 w-3.5 flex-shrink-0" />
                            Pedido: {formatearFecha(anteojo.fecha_pedido)}
                          </div>
                          {anteojo.nro_sobre && (
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <ClipboardDocumentListIcon className="h-3.5 w-3.5 flex-shrink-0" />
                              Sobre: #{anteojo.nro_sobre}
                            </div>
                          )}
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <CurrencyDollarIcon className="h-3.5 w-3.5 flex-shrink-0" />
                            Total: {formatearMoneda(anteojo.precio_total)}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <BanknotesIcon className="h-3.5 w-3.5 flex-shrink-0" />
                            Seña: {formatearMoneda(anteojo.sena)}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <BanknotesIcon className="h-3.5 w-3.5 flex-shrink-0" />
                            Saldo: {formatearMoneda(anteojo.saldo)}
                          </div>
                        </div>
                        {anteojo.observaciones && (
                          <div className="mt-1.5 flex items-center gap-1 text-xs text-gray-500 italic">
                            <ChatBubbleLeftEllipsisIcon className="h-3.5 w-3.5 flex-shrink-0" />
                            {anteojo.observaciones}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Botones */}
                    <div className="flex gap-2 w-full sm:w-auto sm:flex-shrink-0">
                      <button
                        className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 py-2 bg-blue-500 text-white text-xs font-medium rounded hover:bg-blue-600 transition-colors"
                        onClick={() => setDetalleAnteojo(anteojo)}
                      >
                        <EyeIcon className="h-3.5 w-3.5" />
                        Ver detalle
                      </button>
                      <button
                        className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 py-2 bg-red-500 text-white text-xs font-medium rounded hover:bg-red-600 transition-colors"
                        onClick={() => handleEliminarAnteojo(anteojo.id)}
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {/* Modal de detalle de anteojo */}
      {detalleAnteojo && (
        <AnteojoDetalle
          anteojo={detalleAnteojo}
          onClose={() => setDetalleAnteojo(null)}
          onEstadoChange={async () => {
            await cargarAnteojos();
            setDetalleAnteojo(null);
          }}
          onSave={async (data) => {
            await editarAnteojo(data.id, data); // Actualizar en backend
            await cargarAnteojos();
            setDetalleAnteojo(null);
          }}
        />
      )}
    </div>
  );
};

export default AnteojosTodos;
